/**
 * Timezone utilities for the MDM platform.
 * All timestamp display should go through these functions.
 * Uses browser Intl API only — no external dependencies.
 */

/** Returns the browser's detected IANA timezone. */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

/** Validates an IANA timezone string. */
export function isValidTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Absolute datetime with seconds — used for ops tables that need precise timestamps. */
export const DATETIME_WITH_SECONDS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};

/** Formats a UTC ISO string to a localized date/time string in the given timezone. */
export function formatDateTime(
  utcString: string,
  timezone: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!utcString) return "-";
  try {
    const date = new Date(utcString);
    if (isNaN(date.getTime())) return "-";
    const tz = isValidTimezone(timezone) ? timezone : "UTC";
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    };
    return new Intl.DateTimeFormat("en", {
      ...defaultOptions,
      ...options,
    }).format(date);
  } catch {
    return "-";
  }
}

/** Formats a UTC ISO string as a relative time string ("5m ago", "2h ago", etc.). */
export function formatRelativeTime(utcString?: string): string {
  if (!utcString) return "Never";
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "Never";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return ""; // > 7 days: empty signals FormattedTime to use absolute format
}

/** Returns whether a timestamp is less than 7 days old (should use relative format). */
export function isRecent(utcString?: string): boolean {
  if (!utcString) return false;
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() < 7 * 24 * 60 * 60 * 1000;
}

/**
 * List of all IANA timezones supported by the browser.
 * Falls back to a minimal list if Intl.supportedValuesOf is unavailable.
 */
export const TIMEZONE_LIST: string[] = (() => {
  if (
    typeof Intl !== "undefined" &&
    typeof Intl.supportedValuesOf === "function"
  ) {
    try {
      return Intl.supportedValuesOf("timeZone");
    } catch {
      // fallthrough
    }
  }
  // Minimal fallback for older browsers
  return [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Sao_Paulo",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Asia/Ho_Chi_Minh",
    "Asia/Singapore",
    "Asia/Kolkata",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];
})();
